import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { resolveScopedPath } from './householdPaths';
import type { PantryLearningState } from '../redux/pantryLearningSlice';

/**
 * Persistence for what the user has taught the pantry matcher.
 *
 * One small document per scope rather than a collection: the whole thing is a
 * few hundred short strings, it is always read and written in full, and a
 * single doc keeps a correction atomic.
 *
 * Household scope wins when the user is paired, so a lesson learned by one
 * member applies for the other — the same rule the inventory itself follows.
 */
const LEARNING_DOC = ['pantryLearning', 'corrections'];

export type StoredPantryLearning = Pick<
  PantryLearningState,
  'aliases' | 'distinct' | 'covers' | 'notCovers' | 'zones' | 'categories' | 'lastUpdated'
>;

const learningDocRef = (userId?: string, householdId?: string | null) => {
  if (!firestore) return null;
  const segments = resolveScopedPath(userId, householdId, ...LEARNING_DOC);
  if (!segments) return null;
  const [root, id, ...rest] = segments;
  return doc(firestore, root, id, ...rest);
};

/**
 * Read the stored lessons.
 *
 * Returns null rather than throwing when Firebase is unavailable: the matcher
 * works without its lessons, just less well, and the pantry must still open.
 */
export async function fetchPantryLearningFromCloud(
  userId?: string,
  householdId?: string | null
): Promise<StoredPantryLearning | null> {
  if (!isFirebaseEnabled() || !firestore) return null;

  const ref = learningDocRef(userId, householdId);
  if (!ref) return null;

  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) return null;

  const data = snapshot.data();
  return {
    aliases: Array.isArray(data.aliases) ? data.aliases : [],
    distinct: Array.isArray(data.distinct) ? data.distinct : [],
    covers: Array.isArray(data.covers) ? data.covers : [],
    notCovers: Array.isArray(data.notCovers) ? data.notCovers : [],
    zones: data.zones ?? {},
    categories: data.categories ?? {},
    lastUpdated: data.lastUpdated ?? null,
  };
}

/** Write the lessons back. Silently no-ops when there is nowhere to write. */
export async function savePantryLearningToCloud(
  userId: string | undefined,
  householdId: string | null | undefined,
  learning: StoredPantryLearning
): Promise<void> {
  if (!isFirebaseEnabled() || !firestore) return;

  const ref = learningDocRef(userId, householdId);
  if (!ref) return;

  await setDoc(
    ref,
    {
      aliases: learning.aliases,
      distinct: learning.distinct,
      covers: learning.covers,
      notCovers: learning.notCovers,
      zones: learning.zones,
      categories: learning.categories,
      lastUpdated: learning.lastUpdated,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}
